import { useMemo, useState } from "react";
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";

export const CAMPAIGN_STATUS = {
  awaiting_start: { label: "Da avviare", cls: "bg-[#3b82f6] text-white" },
  running: { label: "In corso", cls: "bg-[#10b981] text-white" },
  paused: { label: "In pausa", cls: "bg-[#f59e0b] text-black" },
  completed: { label: "Completata", cls: "bg-[#e4e7e9] text-[#5b6168]" },
  refunded: { label: "Rimborsata", cls: "bg-[#ef4444] text-white" },
  pending_payment: { label: "In attesa pagamento", cls: "bg-[#e4e7e9] text-[#6b7280]" },
};

export const TX_STATUS = {
  paid: { label: "Pagato", cls: "bg-[#10b981] text-white" },
  pending: { label: "In attesa", cls: "bg-[#f59e0b] text-black" },
  refunded: { label: "Rimborsato", cls: "bg-[#ef4444] text-white" },
};

export const Badge = ({ map, value, testid }) => {
  const m = map[value] || { label: value, cls: "bg-[#e4e7e9] text-[#5b6168]" };
  return <span data-testid={testid} className={`text-[10px] font-mono uppercase px-2 py-0.5 whitespace-nowrap ${m.cls}`}>{m.label}</span>;
};

export const fmtDate = (iso, withTime = false) =>
  iso ? new Date(iso).toLocaleString("it-IT", withTime ? { dateStyle: "short", timeStyle: "short" } : { dateStyle: "short" }) : "—";

export const money = (amount, currency = "eur") =>
  `${(currency || "eur").toUpperCase() === "CHF" ? "CHF" : "€"} ${Number(amount || 0).toFixed(2).replace(".00", "")}`;

export const ProgressBar = ({ value, testid }) => (
  <div data-testid={testid} className="h-1.5 bg-[#e4e7e9] w-full min-w-[80px]">
    <div className="h-full aurora-gradient transition-[width] duration-500" style={{ width: `${Math.min(100, value || 0)}%` }} />
  </div>
);

export const useTable = (rows, { pageSize = 10, initialSort, filterFn } = {}) => {
  const [sort, setSort] = useState(initialSort || { key: null, dir: "desc" });
  const [page, setPage] = useState(0);
  const filtered = useMemo(() => (filterFn ? rows.filter(filterFn) : rows), [rows, filterFn]);
  const sorted = useMemo(() => {
    if (!sort.key) return filtered;
    const get = (r) => sort.key.split(".").reduce((o, k) => o?.[k], r);
    return [...filtered].sort((a, b) => {
      const x = get(a) ?? "", y = get(b) ?? "";
      const cmp = typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y));
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [filtered, sort]);
  const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, pages - 1);
  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
  return { rows: sorted.slice(safePage * pageSize, (safePage + 1) * pageSize), total: sorted.length,
    page: safePage, pages, setPage, sort, toggleSort };
};

export const SortTh = ({ label, k, table }) => (
  <th className="px-4 py-3">
    <button data-testid={`sort-${k}`} onClick={() => table.toggleSort(k)} className="inline-flex items-center gap-1 uppercase tracking-widest hover:text-[#E0262C]">
      {label}
      {table.sort.key === k && (table.sort.dir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
    </button>
  </th>
);

export const Pager = ({ table, testid }) => (
  <div data-testid={testid} className="flex items-center justify-between px-4 py-3 border-t border-[#d5dbde] text-xs font-mono text-[#6b7280]">
    <span>{table.total} risultati</span>
    <div className="flex items-center gap-2">
      <button data-testid={`${testid}-prev`} disabled={table.page === 0} onClick={() => table.setPage(table.page - 1)}
        className="w-7 h-7 border border-[#d5dbde] flex items-center justify-center disabled:opacity-30 hover:border-[#E0262C]"><ChevronLeft className="w-3.5 h-3.5" /></button>
      <span>{table.page + 1} / {table.pages}</span>
      <button data-testid={`${testid}-next`} disabled={table.page >= table.pages - 1} onClick={() => table.setPage(table.page + 1)}
        className="w-7 h-7 border border-[#d5dbde] flex items-center justify-center disabled:opacity-30 hover:border-[#E0262C]"><ChevronRight className="w-3.5 h-3.5" /></button>
    </div>
  </div>
);

export const selectCls = "h-10 bg-[#f4f6f7] border border-[#d5dbde] text-[#2B3036] rounded-sm text-sm px-3";
